import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { allSitePages, siteTemplates } from '../data/siteIndex';
import { usePageMeta } from '../hooks/usePageMeta';
const ALL = 'all';
const PAGE_SIZE = 60;

/**
 * Searchable index of every template-driven page on the site.
 *
 * The degree-program template alone contributes several hundred URLs, so this
 * is search-first: filter by template, then narrow by text. Results are capped
 * and paged rather than rendering everything at once.
 */
export function SiteIndexPage() {
  usePageMeta('All Pages | Online College Guide', 'Search every guide, article, program page, and partner college on Online College Guide.');
  const [query, setQuery] = useState('');
  const [templateId, setTemplateId] = useState(ALL);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allSitePages.filter(page => {
      if (templateId !== ALL && page.templateId !== templateId) return false;
      if (!q) return true;
      return [page.label, page.detail, page.keywords, page.templateLabel].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [query, templateId]);
  const isFiltered = Boolean(query.trim()) || templateId !== ALL;
  const resetVisible = () => setVisible(PAGE_SIZE);
  const clearFilters = () => {
    setQuery('');
    setTemplateId(ALL);
    resetVisible();
  };
  const activeTemplate = siteTemplates.find(t => t.id === templateId);
  return <main className="bg-white" data-id="element-2805" data-mp="37vs1ltb9q1c2">
      {/* Hero */}
      <header className="hero-bg" data-id="element-2806" data-mp="3ctjuapjqlrhb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2807" data-mp="3culgnq67e0ec">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-2808" data-mp="2yt91vi2s44u1">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2809" data-mp="2yvvj69fh1ypg">
              <li data-id="element-2810" data-mp="32uuhvcam0z8g">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-2811" data-mp="32x70lro6zdmk">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2812" data-mp="14lnbk8dyjk73">/</li>
              <li className="text-white/90" data-id="element-2813" data-mp="14km1a64rn1qf">All Pages</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-2814" data-mp="znde1r4rg7ib">
            Browse Every Page
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-2815" data-mp="1sc17wb3ks57h">
            Every guide, article, program page, degree category, and partner
            college in one searchable index.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-2816" data-mp="1ibqryiq5ojom">
        {/* Search + template filter */}
        <div className="mb-8" data-id="element-2817" data-mp="1edt3ji47m1ma">
          <label htmlFor="site-search" className="sr-only" data-id="element-2818" data-mp="6xwtffndtuwn">
            Search pages
          </label>
          <div className="relative max-w-xl" data-id="element-2819" data-mp="2wteqwh2m4x0">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-2820" data-mp="2v065bmwdamd" />
            <input id="site-search" type="search" value={query} onChange={e => {
            setQuery(e.target.value);
            resetVisible();
          }} placeholder="Search every page — try “nursing” or “accreditation”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-2821" data-mp="2imbdiklthlce" />
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4" role="group" aria-label="Filter by template" data-id="element-2822" data-mp="36bvx95psommq">
            {[{
            id: ALL,
            label: 'All',
            count: allSitePages.length
          }, ...siteTemplates.map(t => ({
            id: t.id,
            label: t.label,
            count: t.pages.length
          }))].map(chip => {
            const isActive = templateId === chip.id;
            return <button key={chip.id} type="button" onClick={() => {
              setTemplateId(chip.id);
              resetVisible();
            }} aria-pressed={isActive} className={`px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors duration-200 ${isActive ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy hover:text-navy'}`} data-id="element-2823" data-mp="gozlmk1mdjdg">
                  {chip.label}
                  <span className={`ml-1.5 font-normal ${isActive ? 'text-white/70' : 'text-gray-400'}`} data-id="element-2824" data-mp="3g9lbtj5fi8ng">
                    {chip.count.toLocaleString()}
                  </span>
                </button>;
          })}
          </div>

          {activeTemplate && <p className="mt-4 text-sm text-gray-600 max-w-2xl" data-id="element-2825" data-mp="1vlsqockelwju">
              {activeTemplate.description}{' '}
              <span className="font-mono text-xs text-gray-400" data-id="element-2826" data-mp="1ltwputqxnsbx">
                {activeTemplate.pattern}
              </span>
              {activeTemplate.indexTo && <>
                  {' '}
                  <Link to={activeTemplate.indexTo} className="text-navy font-medium hover:underline" data-id="element-2827" data-mp="2ejdav6c846y4">
                    View index page
                  </Link>
                </>}
            </p>}

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-2828" data-mp="p45jnx9a4gqa">
            <span aria-live="polite" data-id="element-2829" data-mp="p69vr2hc0m1n">
              {results.length.toLocaleString()}{' '}
              {results.length === 1 ? 'page' : 'pages'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-2830" data-mp="2vu0il40y2zeq">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2831" data-mp="2m0080mmw91ym" />
                Clear filters
              </button>}
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-2832" data-mp="28224yde0wryf">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-2833" data-mp="233idxykkopz7">
              No pages match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-2834" data-mp="1y4xj6eo3ayi5">
              Try a broader term, or get matched with accredited programs
              directly.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-2835" data-mp="3sioo50vh4u79">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2836" data-mp="3sgm02mdvtb56">
                Show all pages
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-2837" data-mp="ilg982aumdat">
                Get Matched Now
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2838" data-mp="3m49c9l603jqq" />
              </Link>
            </div>
          </div> : <>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2839" data-mp="3w1dyfkzujf7j">
              {results.slice(0, visible).map(page => <li key={`${page.templateId}-${page.to}`} data-id="element-2840" data-mp="16d4wv5cq6dlf">
                  <Link to={page.to} className="group flex flex-col h-full border border-gray-200 rounded-lg px-4 py-3 hover:border-navy hover:bg-light transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2841" data-mp="1b92h3mr4043h">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-crimson mb-1.5" data-id="element-2842" data-mp="2650ty3ukuduc">
                      {page.templateLabel}
                    </span>
                    <span className="flex items-start justify-between gap-3" data-id="element-2843" data-mp="2g1ncun3ceutq">
                      <span className="text-sm font-semibold text-navy group-hover:underline" data-id="element-2844" data-mp="2b32c8mk6i94r">
                        {page.label}
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy" aria-hidden="true" data-id="element-2845" data-mp="2p2bj2vmhvqw2" />
                    </span>
                    {page.detail && <span className="mt-1 text-xs text-gray-500 line-clamp-2" data-id="element-2846" data-mp="31d2rh7dgrg3g">
                        {page.detail}
                      </span>}
                  </Link>
                </li>)}
            </ul>

            {visible < results.length && <div className="text-center mt-8" data-id="element-2847" data-mp="2siy5b199ysu9">
                <button type="button" onClick={() => setVisible(n => n + PAGE_SIZE)} className="px-6 py-3 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2848" data-mp="2wlb0ivqeb8cs">
                  Show more ({(results.length - visible).toLocaleString()}{' '}
                  remaining)
                </button>
              </div>}
          </>}
      </div>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-2849" data-mp="7v1ppiguuozj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-2850" data-mp="7sp6l1zd28kw">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-2851" data-mp="bu11nn107m0h">
            Can't find what you're looking for?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-2852" data-mp="2wqkdk3ps1ysx">
            Answer a few questions about your goals, schedule, and budget and
            we'll connect you with accredited programs that fit. No cost, no
            obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-2853" data-mp="2n0ir1gd2oog4">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2854" data-mp="31pwxaflcqpra" />
          </Link>
        </div>
      </section>
    </main>;
}